'use client'

import Script from 'next/script'
import React, { useEffect, useState } from 'react'

/**
 * Lazy-loads TapPay's JS SDK + calls `setupSDK` exactly once per app
 * mount. Children read `ready` via the context provider to know when
 * they may interact with `window.TPDirect.card`.
 *
 * Env vars (all NEXT_PUBLIC_*, shipped to the browser by design):
 *   NEXT_PUBLIC_TAPPAY_APP_ID
 *   NEXT_PUBLIC_TAPPAY_APP_KEY
 *   NEXT_PUBLIC_TAPPAY_ENV   ('sandbox' | 'production')
 *
 * Ported from kiao/apps/shop's tappay/script/index.tsx; simplified to
 * one-shot setup (no Apple Pay / Line Pay).
 */
interface Props {
  /** Children rendered only after the SDK has been set up. */
  children: (ready: boolean) => React.ReactNode
}

const TAPPAY_SDK_URL = 'https://js.tappaysdk.com/sdk/tpdirect/v5.17.1'

export function TappayScript({ children }: Props) {
  // Initialize from an already-loaded SDK. Next's <Script onLoad> only
  // fires once per page-load, so a component that unmounts and remounts
  // (e.g. the /pricing checkout modal, which is conditionally rendered)
  // would otherwise never see onLoad again and stay stuck at ready=false.
  // Checking window.TPDirect on mount makes remounts pick up the loaded
  // SDK immediately. Safe for hydration: on the very first load TPDirect
  // isn't present yet, so this matches the server-rendered false.
  const [scriptLoaded, setScriptLoaded] = useState(
    () => typeof window !== 'undefined' && Boolean(window.TPDirect)
  )
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (!scriptLoaded) return
    if (typeof window === 'undefined' || !window.TPDirect) return

    const appIdRaw = process.env.NEXT_PUBLIC_TAPPAY_APP_ID
    const appKey = process.env.NEXT_PUBLIC_TAPPAY_APP_KEY
    const env = (process.env.NEXT_PUBLIC_TAPPAY_ENV ?? 'sandbox') as 'sandbox' | 'production'

    const appId = appIdRaw ? Number(appIdRaw) : Number.NaN
    if (!Number.isFinite(appId) || !appKey) {
      // Misconfiguration: NEXT_PUBLIC_TAPPAY_* not set. The card form will
      // refuse to mount; surface a clearer state to children rather than
      // calling setupSDK with NaN.
      return
    }

    window.TPDirect.setupSDK(appId, appKey, env)
    setReady(true)
  }, [scriptLoaded])

  return (
    <>
      <Script
        src={TAPPAY_SDK_URL}
        strategy="afterInteractive"
        onLoad={() => setScriptLoaded(true)}
      />
      {children(ready)}
    </>
  )
}
